import { useQuery } from '@tanstack/react-query'

import { formatDate, formatMoneyShort } from '../lib/format'
import { overviewApi, type PortalMe } from './api'

/**
 * Первый экран кабинета: тариф, сроки и как идут обращения.
 *
 * Сюда человек заходит, чтобы за десять секунд понять две вещи: до какого
 * числа оплачено и не забыли ли про его вопросы. Всё остальное — ниже.
 */
export default function PortalOverview({
  me,
  onOpenTickets,
}: {
  me: PortalMe
  onOpenTickets: () => void
}) {
  const data = useQuery({ queryKey: ['portal', 'overview'], queryFn: overviewApi.get })

  if (data.isLoading) return <div className="empty">Загрузка…</div>

  const o = data.data
  const sub = o?.subscription ?? null
  const usage = o?.usage ?? null
  const stats = o?.tickets

  const hoursLeft = usage?.hours_left ? Number(usage.hours_left) : null
  const hoursIncluded = usage?.hours_included ? Number(usage.hours_included) : null
  const usedShare =
    hoursIncluded && hoursIncluded > 0
      ? Math.min(100, Math.round((Number(usage!.hours_used) / hoursIncluded) * 100))
      : null

  const months = Object.entries(stats?.by_month ?? {})
  const maxMonth = Math.max(1, ...months.map(([, n]) => n))

  return (
    <>
      <h1 className="page-title">{me.client_name}</h1>

      <div className="pv-cards">
        <div className="pv-card">
          <div className="pv-label">Тариф</div>
          {sub ? (
            <>
              <div className="pv-value">{sub.tariff_name}</div>
              <div className="pv-sub">{formatMoneyShort(Number(sub.price))} в месяц</div>
            </>
          ) : (
            <>
              <div className="pv-value pv-muted">Не назначен</div>
              <div className="pv-sub">Уточните у вашего менеджера</div>
            </>
          )}
        </div>

        <div className="pv-card">
          <div className="pv-label">Подписка</div>
          {sub ? (
            <>
              <div className="pv-value">
                {sub.ends_on ? `до ${formatDate(sub.ends_on)}` : 'бессрочно'}
              </div>
              <div className="pv-sub">
                {sub.days_left === null
                  ? `с ${formatDate(sub.started_on)}`
                  : sub.days_left >= 0
                    ? `осталось дней: ${sub.days_left}`
                    : 'срок истёк'}
              </div>
            </>
          ) : (
            <div className="pv-value pv-muted">—</div>
          )}
        </div>

        <div className="pv-card">
          <div className="pv-label">Открытых обращений</div>
          <div className="pv-value">{stats?.open_now ?? 0}</div>
          <div className="pv-sub">
            <button className="link-btn" onClick={onOpenTickets}>
              Посмотреть
            </button>
          </div>
        </div>

        <div className="pv-card">
          <div className="pv-label">Среднее время до ответа</div>
          <div className="pv-value">
            {stats?.avg_first_response_hours != null
              ? `${stats.avg_first_response_hours} ч`
              : '—'}
          </div>
          <div className="pv-sub">по всем обращениям</div>
        </div>
      </div>

      {usage && hoursIncluded !== null && (
        <>
          <h2 className="section-title">Часы по тарифу</h2>
          <p className="hint">
            Считаются работы по абонентскому договору за период с{' '}
            {formatDate(usage.period_from)}. Разовые работы оплачиваются отдельно и
            включённые часы не тратят.
          </p>
          <div className="pv-bar-wrap">
            <div className="pv-bar">
              <div
                className={`pv-bar-fill${usedShare! >= 90 ? ' pv-bar-warn' : ''}`}
                style={{ width: `${usedShare}%` }}
              />
            </div>
            <div className="pv-bar-legend">
              <span>
                Использовано <strong>{Number(usage.hours_used)} ч</strong> из{' '}
                {hoursIncluded} ч
              </span>
              <span className={hoursLeft !== null && hoursLeft <= 0 ? 'pv-over' : undefined}>
                {hoursLeft !== null && hoursLeft > 0
                  ? `Осталось ${hoursLeft} ч`
                  : 'Включённые часы израсходованы'}
              </span>
            </div>
          </div>
        </>
      )}

      {months.length > 0 && (
        <>
          <h2 className="section-title">Обращения по месяцам</h2>
          <div className="pv-chart">
            {months.map(([label, count]) => (
              <div key={label} className="pv-col" title={`${label}: ${count}`}>
                <div className="pv-col-value">{count || ''}</div>
                <div
                  className="pv-col-bar"
                  style={{ height: `${Math.round((count / maxMonth) * 100)}%` }}
                />
                <div className="pv-col-label">{label}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {stats && Object.keys(stats.by_author).length > 0 && (
        <>
          <h2 className="section-title">Кто обращался</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th className="num">Обращений</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.by_author).map(([name, count]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td className="num mono">{count}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700 }}>
                  <td>Всего</td>
                  <td className="num mono">{stats.total}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}
